import { useCallback, useEffect, useRef, useState } from "react";

import debounce from "lodash.debounce";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { useToast } from "hooks/toast";

import { isProduct, ProductId } from "multitenancy/product";

import { hideLoading, showLoading } from "store/modules/loading/actions";
import { useAppSelector } from "store/rootReducer";

import { AdminArea } from "components/Body/AdminArea";
import { Drawer } from "components/Drawer";

import { UnitService } from "services/place";

import { LogOperation } from "utils/entities/logOperation";
import { FORMS_ADMIN } from "utils/entities/permission";
import { stringFormat } from "utils/stringFormat";

import { CreateEditUnit } from "./CreateEdit";

interface IntegrationForeignKeyApi {
  IntegrationName: string;
  ForeignKeyId: string;
}

export interface UnitInterface {
  id: number;
  foreignKey: string | IntegrationForeignKeyApi[] | null;
  name: string;
  description: string;
  city: string;
  address: string;
  addressNumber: number;
  phone: string;
  district: number;
  codeDistrict: number;
  customerId: number;
  lat: number;
  lon: number;
  extraContent: string;
  createIn: Date;
  updateIn: Date;
  active: boolean;
  type: number;
}

interface IntegrationForeignKeyForm {
  integrationName: string;
  foreignKeyId: string;
}

interface LegacyForeignKeyPayload {
  integrationName?: string;
  IntegrationName?: string;
  name?: string;
  foreignKeyId?: string;
  ForeignKeyId?: string;
  value?: string;
}

export const isUnitContextProduct =
  isProduct(ProductId.Cartorio) || isProduct(ProductId.Inquerito);

const BASE_INTEGRATION_FOREIGN_KEYS: IntegrationForeignKeyForm[] =
  isUnitContextProduct
    ? [{ integrationName: "unitExternalCode", foreignKeyId: "" }]
    : [
        { integrationName: "PJE", foreignKeyId: "" },
        { integrationName: "DCP", foreignKeyId: "" },
        { integrationName: "EPROC", foreignKeyId: "" },
      ];

const createDefaultIntegrationForeignKeys = () =>
  BASE_INTEGRATION_FOREIGN_KEYS.map((item) => ({ ...item }));

const mergeIntegrationForeignKeys = (
  entries: IntegrationForeignKeyForm[] | undefined | null,
) => {
  const baseMap = new Map<string, IntegrationForeignKeyForm>();

  BASE_INTEGRATION_FOREIGN_KEYS.forEach((item) => {
    baseMap.set(item.integrationName, { ...item });
  });

  entries?.forEach((entry) => {
    if (!entry?.integrationName) return;
    baseMap.set(entry.integrationName, {
      integrationName: entry.integrationName,
      foreignKeyId: entry.foreignKeyId ?? "",
    });
  });

  const baseOrder = BASE_INTEGRATION_FOREIGN_KEYS.map(
    (item) => item.integrationName,
  );
  const result: IntegrationForeignKeyForm[] = baseOrder.map((name) => {
    const value = baseMap.get(name);
    return value ? { ...value } : { integrationName: name, foreignKeyId: "" };
  });

  entries
    ?.filter(
      (entry) =>
        entry?.integrationName && !baseOrder.includes(entry.integrationName),
    )
    .forEach((entry) => {
      result.push({
        integrationName: entry.integrationName,
        foreignKeyId: entry.foreignKeyId ?? "",
      });
    });

  return result;
};

const parseIntegrationForeignKeys = (
  value:
    | string
    | IntegrationForeignKeyApi[]
    | IntegrationForeignKeyForm[]
    | null
    | undefined,
): IntegrationForeignKeyForm[] => {
  if (!value) {
    return createDefaultIntegrationForeignKeys();
  }

  let source: unknown = value;
  try {
    if (!Array.isArray(source) && typeof value === "string") {
      source = JSON.parse(value);
    }
  } catch {
    source = null;
  }

  if (isUnitContextProduct) {
    if (Array.isArray(source)) {
      const legacySource = source as LegacyForeignKeyPayload[];
      const firstEntry = legacySource.find(
        (item) =>
          item?.foreignKeyId ?? item?.ForeignKeyId ?? item?.value,
      );
      const code =
        firstEntry?.foreignKeyId ??
        firstEntry?.ForeignKeyId ??
        firstEntry?.value ??
        "";
      return [{ integrationName: "unitExternalCode", foreignKeyId: code }];
    }
    if (typeof value === "string") {
      return [{ integrationName: "unitExternalCode", foreignKeyId: value }];
    }
    return createDefaultIntegrationForeignKeys();
  }

  if (Array.isArray(source)) {
    const legacySource = source as LegacyForeignKeyPayload[];
    const normalized = legacySource
      .map((item) => ({
        integrationName:
          item?.integrationName ?? item?.IntegrationName ?? item?.name ?? "",
        foreignKeyId:
          item?.foreignKeyId ?? item?.ForeignKeyId ?? item?.value ?? "",
      }))
      .filter((item) => item.integrationName);
    return mergeIntegrationForeignKeys(normalized);
  }

  if (typeof value === "string") {
    return mergeIntegrationForeignKeys([
      {
        integrationName: BASE_INTEGRATION_FOREIGN_KEYS[0].integrationName,
        foreignKeyId: value,
      },
    ]);
  }

  return createDefaultIntegrationForeignKeys();
};

const buildForeignKeyPayload = (
  foreignKeys: IntegrationForeignKeyForm[],
): string | null => {
  const sanitized: IntegrationForeignKeyApi[] = foreignKeys
    .map((item) => ({
      IntegrationName: item.integrationName,
      ForeignKeyId: item.foreignKeyId ? item.foreignKeyId.trim() : "",
    }))
    .filter(
      (item) =>
        item.IntegrationName &&
        item.ForeignKeyId &&
        !stringFormat.isEmptyOrSpaces(item.ForeignKeyId),
    );

  return sanitized.length > 0 ? JSON.stringify(sanitized) : null;
};

export function Unit() {
  const { t } = useTranslation();
  const [element, setElement] = useState<UnitInterface>();
  const [name, setName] = useState("");
  const [active, setActive] = useState(true);
  const [description, setDescription] = useState("");
  const [city, setCity] = useState("");
  const [address, setAddress] = useState("");
  const [addressNumber, setAddressNumber] = useState<number>();
  const [phone, setPhone] = useState("");
  const [unit, setUnit] = useState(false);
  const [codeDistrict, setCodeDistrict] = useState(0);
  const [loading, setLoading] = useState(true);
  const [extraContent, setExtraContent] = useState("");
  const [latLng, setLatLng] = useState("");
  const [integrationForeignKeys, setIntegrationForeignKeys] = useState<
    IntegrationForeignKeyForm[]
  >(createDefaultIntegrationForeignKeys());
  const [listUnit, setListUnit] = useState<any>([]);
  const [openModal, setOpenModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [search, setSearch] = useState("");
  const [situation, setSituation] = useState<number | null>(2);
  const [fieldErrorName, setFieldErrorName] = useState(false);
  const [fieldErrorDescription, setFieldErrorDescription] = useState(false);
  const [fieldErrorCity, setFieldErrorCity] = useState(false);
  const [fieldErrorAddress, setFieldErrorAddress] = useState(false);
  const [fieldErrorAddressNumber, setFieldErrorAddressNumber] = useState(false);
  const [fieldErrorPhone, setFieldErrorPhone] = useState(false);
  const [fieldErrorExtraContent, setFieldErrorExtraContent] = useState(false);
  const [fieldErrorCodeDistrict, setFieldErrorCodeDistrict] = useState(false);

  const [currentPage, setCurrentPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const { user } = useAppSelector((state) => state.user);
  const { globalConfigurations } = useSelector(
    (state: any) => state.configurations,
  );

  useEffect(() => {
    const paginationList = Array.isArray(globalConfigurations)
      ? globalConfigurations?.find((x: any) => x?.name === "PaginationList")
      : null;
    const paginationValue = paginationList?.value
      ? parseInt(paginationList.value)
      : 10;
    setPageSize(paginationValue);
  }, [globalConfigurations]);

  function validateFormFields() {
    const validations: Array<{
      isInvalid: boolean;
      setter: (v: boolean) => void;
    }> = [
      { isInvalid: !name || stringFormat.isEmptyOrSpaces(name), setter: setFieldErrorName },
      { isInvalid: !description || stringFormat.isEmptyOrSpaces(description), setter: setFieldErrorDescription },
      { isInvalid: !city || stringFormat.isEmptyOrSpaces(city), setter: setFieldErrorCity },
      { isInvalid: !address || stringFormat.isEmptyOrSpaces(address), setter: setFieldErrorAddress },
      { isInvalid: !addressNumber || addressNumber < 0, setter: setFieldErrorAddressNumber },
      { isInvalid: !phone || stringFormat.isEmptyOrSpaces(phone), setter: setFieldErrorPhone },
      { isInvalid: !extraContent || stringFormat.isEmptyOrSpaces(extraContent), setter: setFieldErrorExtraContent },
      { isInvalid: !unit && (!codeDistrict || codeDistrict === 0), setter: setFieldErrorCodeDistrict },
    ];

    let allValid = true;
    for (const { isInvalid, setter } of validations) {
      setter(isInvalid);
      if (isInvalid) allValid = false;
    }
    return allValid;
  }

  const dispatch = useDispatch();
  const { addToast } = useToast();

  const fetchIdRef = useRef(0);

  useEffect(() => {
    if (pageSize > 0) {
      setCurrentPage(0);
      onLoadList(0);
    }
  }, [situation, pageSize]);

  async function onLoadList(page?: number, searchTerm?: string) {
    const fetchId = ++fetchIdRef.current;
    try {
      setLoading(true);
      dispatch(showLoading());
      const response = await UnitService.getAllPaginated({
        pageNumber: (page ?? currentPage) + 1,
        pageSize,
        searchTerm: searchTerm ?? search,
        active: situation ?? 2,
      });
      if (fetchId !== fetchIdRef.current) return;
      setListUnit(response?.data?.data?.items ?? response?.data?.data ?? []);
      setTotalCount(
        response?.data?.data?.totalCount ?? response?.data?.totalCount ?? 0,
      );
    } catch (error) {
      if (
        typeof error === "object" &&
        error !== null &&
        "response" in error &&
        typeof (error as any).response?.status === "number"
      ) {
        const status = (error as any).response.status;

        if (status === 403) {
          addToast({
            type: "error",
            title: t("common.accessDenied"),
            description: t("units.fetchError"),
          });
        }
      }

      console.error("Erro ao carregar a lista de unidades:", error);
    } finally {
      if (fetchId === fetchIdRef.current) {
        setLoading(false);
        dispatch(hideLoading());
      }
    }
  }

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setCurrentPage(0);
      onLoadList(0, value);
    }, 400),
    [situation, pageSize],
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleSearchChange = (text: string) => {
    setSearch(text);
    debouncedSearch(text);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    onLoadList(page);
  };

  const handleSituationChange = (value: any) => {
    setSituation(value);
    setCurrentPage(0);
  };

  const serverPageCount = Math.ceil(totalCount / pageSize);

  async function clear() {
    setIsEdit(false);
    setName("");
    setActive(true);
    setElement(undefined);
    setDescription("");
    setCity("");
    setAddress("");
    setAddressNumber(undefined);
    setPhone("");
    setUnit(true);
    setCodeDistrict(0);
    setExtraContent("");
    setLatLng("");
    setIntegrationForeignKeys(createDefaultIntegrationForeignKeys());
    setOpenModal(false);
    setFieldErrorName(false);
    setFieldErrorDescription(false);
    setFieldErrorCity(false);
    setFieldErrorAddress(false);
    setFieldErrorAddressNumber(false);
    setFieldErrorPhone(false);
    setFieldErrorExtraContent(false);
    setFieldErrorCodeDistrict(false);
  }

  function openModalClearAdd() {
    clear();
    setOpenModal(true);
  }

  async function onClick(item: UnitInterface) {
    setIsEdit(true);
    setName(item.name);
    setActive(item.active);
    setDescription(item.description);
    setCity(item.city);
    setAddress(item.address);
    setAddressNumber(item.addressNumber);
    setPhone(item.phone);
    setUnit(item.district === 0 ? false : true);
    setCodeDistrict(item.codeDistrict);
    setExtraContent(item.extraContent);
    setIntegrationForeignKeys(parseIntegrationForeignKeys(item.foreignKey));
    setLatLng(item.lat + "," + item.lon);
    setElement(item);
    setOpenModal(true);
  }

  function buildForeignKey() {
    if (isUnitContextProduct) {
      return unit
        ? null
        : integrationForeignKeys[0]?.foreignKeyId?.trim() || null;
    }
    return buildForeignKeyPayload(integrationForeignKeys);
  }

  function createBody() {
    return {
      id: isEdit ? element?.id : 0,
      customerId: user?.customerId,
      active: active,
      name: name.trimStart().trimEnd(),
      description: description.trimStart().trimEnd(),
      city: city.trimStart().trimEnd(),
      address: address.trimStart().trimEnd(),
      addressNumber: addressNumber,
      phone: phone ? phone.replace(/\D/g, "") : "",
      lat: 0,
      lon: 0,
      extraContent:
        !extraContent || stringFormat.isEmptyOrSpaces(extraContent)
          ? null
          : extraContent.trimStart().trimEnd(),
      foreignKey: buildForeignKey(),
      createIn: isEdit ? element?.createIn : new Date(),
      updateIn: new Date(),
      type: unit ? 2 : 0,
      device: LogOperation.getDevice(),
      ip: LogOperation.getMachineIP(),
      logPoliceUnitId: LogOperation.getPoliceUnitId(),
      logUserId: LogOperation.getUserId(),
      codeDistrict: unit ? 0 : codeDistrict,
      district: unit ? 1 : 0,
    };
  }

  const handleChangeIntegrationForeignKey = (
    integrationName: string,
    value: string,
  ) => {
    setIntegrationForeignKeys((prev) => {
      const exists = prev.find(
        (item) => item.integrationName === integrationName,
      );

      if (exists) {
        return prev.map((item) =>
          item.integrationName === integrationName
            ? { ...item, foreignKeyId: value }
            : item,
        );
      }

      return [
        ...prev,
        {
          integrationName,
          foreignKeyId: value,
        },
      ];
    });
  };

  async function post() {
    const resultValidation = validateFormFields();
    if (!resultValidation) {
      addToast({
        type: "warning",
        title: t("units.title"),
        description: t("units.fillRequiredFieldsCreate"),
      });
      return;
    }

    if (!addressNumber || addressNumber < 1) {
      addToast({
        type: "warning",
        title: t("units.title"),
        description: t("units.numberMustBePositive"),
      });
      return;
    }

    const existName = stringFormat.verifyExistNameInArray(listUnit, name);

    if (existName) {
      addToast({
        type: "warning",
        title: t("common.warning"),
        description: t("units.nameAlreadyExists"),
      });
      setFieldErrorName(true);
      return;
    }

    dispatch(showLoading());
    setLoading(true);

    const resultPost = await UnitService.post(createBody());
    if (resultPost.status === true) {
      addToast({
        type: "success",
        title: t("units.registered"),
        description: t("units.registeredSuccess"),
      });
      clear();
      await onLoadList();
    } else {
      dispatch(hideLoading());
      if (!resultPost.status) {
        addToast({
          type: resultPost.severity,
          title: t("common.error"),
          description: resultPost.message,
        });
        return;
      }
      addToast({
        type: "error",
        title: t("units.createFailed"),
      });
      return;
    }

    setOpenModal(false);
    dispatch(hideLoading());
    setLoading(false);
  }

  async function put() {
    const resultValidation = validateFormFields();
    if (!resultValidation) {
      addToast({
        type: "warning",
        title: t("units.title"),
        description: t("units.fillRequiredFieldsUpdate"),
      });
      return;
    }

    if (!addressNumber || addressNumber < 1) {
      addToast({
        type: "warning",
        title: t("units.title"),
        description: t("units.numberMustBePositive"),
      });
      return;
    }

    const existName = stringFormat.verifyExistNameInArray(
      listUnit,
      name,
      element?.id,
    );

    if (existName) {
      addToast({
        type: "warning",
        title: t("common.warning"),
        description: t("units.nameAlreadyExists"),
      });
      return;
    }

    const resultPut = await UnitService.put(createBody());
    setLoading(true);
    if (resultPut.status === true) {
      addToast({
        type: "success",
        title: t("units.updated"),
        description: t("units.updatedSuccess"),
      });
      await clear();
      await onLoadList();
      dispatch(hideLoading());
    } else {
      if (!resultPut.status) {
        addToast({
          type: resultPut.severity,
          title: t("common.error"),
          description: resultPut.message,
        });
      } else {
        addToast({
          type: "warning",
          title: t("units.updateFailed"),
        });
      }
      dispatch(hideLoading());
    }
    setLoading(false);
    setOpenModal(false);
  }

  return (
    <>
      <AdminArea
        title={t("units.title")}
        InputSearchLabel={t("common.searchBy")}
        InputSearchOnChange={handleSearchChange}
        searchText={search}
        list={listUnit}
        headerAddButtonOnClick={() => openModalClearAdd()}
        headerTrashButtonOnClick={function (): unknown {
          throw new Error("Function not implemented.");
        }}
        onClick={(item: any) => onClick(item)}
        setItemReturnClick={setElement}
        setOpenModal={setOpenModal}
        setIsEdit={setIsEdit}
        onChangeSituation={handleSituationChange}
        permissionName={FORMS_ADMIN.PLACE}
        loading={loading}
        serverSidePagination
        totalCount={totalCount}
        currentPage={currentPage}
        serverPageCount={serverPageCount}
        onPageChange={handlePageChange}
      />
      <Drawer
        title={isEdit ? t("units.editUnit") : t("units.createUnit")}
        paddingLeft={33}
        open={openModal}
        onClose={() => clear()}
        onApply={isEdit ? put : post}
      >
        <CreateEditUnit
          name={name}
          onChangeName={(e) => {
            setName(e);
            if (stringFormat.isEmptyOrSpaces(e)) return setFieldErrorName(true);
            setFieldErrorName(false);
          }}
          description={description}
          onChangeDescription={(e) => {
            setDescription(e);
            if (stringFormat.isEmptyOrSpaces(e))
              return setFieldErrorDescription(true);
            setFieldErrorDescription(false);
          }}
          city={city}
          onChangeCity={(e) => {
            setCity(e);
            if (stringFormat.isEmptyOrSpaces(e)) return setFieldErrorCity(true);
            setFieldErrorCity(false);
          }}
          address={address}
          onChangeAddress={(e) => {
            setAddress(e);
            if (stringFormat.isEmptyOrSpaces(e))
              return setFieldErrorAddress(true);
            setFieldErrorAddress(false);
          }}
          addressNumber={addressNumber}
          onChangeAddressNumber={(e) => {
            setAddressNumber(e);
            if (stringFormat.isEmptyOrSpaces(e))
              return setFieldErrorAddressNumber(true);
            setFieldErrorAddressNumber(false);
          }}
          phone={phone}
          onChangePhone={(e) => {
            setPhone(e);
            if (stringFormat.isEmptyOrSpaces(e))
              return setFieldErrorPhone(true);
            setFieldErrorPhone(false);
          }}
          unit={unit}
          onChangeUnit={() => {
            setUnit(!unit);
            setCodeDistrict(0);
            setFieldErrorCodeDistrict(false);
          }}
          extraContent={extraContent}
          onChangeExtraContent={(e) => {
            setExtraContent(e);
            if (stringFormat.isEmptyOrSpaces(e))
              return setFieldErrorExtraContent(true);
            setFieldErrorExtraContent(false);
          }}
          active={active}
          onChangeSwitch={() => setActive(!active)}
          latLng={latLng}
          onChangeLatLng={(e) => setLatLng(e)}
          integrationForeignKeys={integrationForeignKeys}
          onChangeIntegrationForeignKey={handleChangeIntegrationForeignKey}
          setCodeDistrict={setCodeDistrict}
          codeDistrict={codeDistrict}
          fieldErrorName={fieldErrorName}
          fieldErrorDescription={fieldErrorDescription}
          fieldErrorCity={fieldErrorCity}
          fieldErrorAddress={fieldErrorAddress}
          fieldErrorAddressNumber={fieldErrorAddressNumber}
          fieldErrorPhone={fieldErrorPhone}
          fieldErrorExtraContent={fieldErrorExtraContent}
          fieldErrorCodeDistrict={fieldErrorCodeDistrict}
        />
      </Drawer>
    </>
  );
}
